import { computed } from 'vue'
import type {
  ProductSearchQuery,
  AttributeWithBooleanValueFilter,
  AttributeWithValuesFilter,
} from '@scayle/storefront-nuxt'
import type { LocationQuery } from 'vue-router'
import { useRoute } from '#app/composables/router'

const NON_FILTER_QUERY_KEYS = new Set([
  'term',
  'page',
  'sort',
  'searchMode',
  'q',
  'gclid',
  'fbclid',
  'msclkid',
  'dclid',
  'twclid',
  'li_fat_id',
  'ref',
])

const getAttributeValues = (value: string | string[]): Array<string> => {
  const values = Array.isArray(value) ? value : [value]

  return values
    .flatMap((entry) => entry.split(','))
    .map((entry) => entry.trim())
    .filter(Boolean)
}

export const parseOdoscopeSearchAppliedFilters = (
  query: LocationQuery,
): ProductSearchQuery => {
  const productSearchQuery: ProductSearchQuery = { attributes: [] }

  Object.entries(query).forEach(([filterName, queryValue]) => {
    if (
      NON_FILTER_QUERY_KEYS.has(filterName) ||
      filterName.startsWith('utm_')
    ) {
      return
    }

    const values = getAttributeValues(
      (Array.isArray(queryValue)
        ? queryValue.filter((entry): entry is string => Boolean(entry))
        : queryValue || '') as string | string[],
    )

    if (values.length === 0) {
      return
    }

    switch (filterName) {
      case 'minPrice':
      case 'maxPrice':
        if (isNaN(Number(values[0]))) {
          return
        }
        productSearchQuery[filterName] = Number(values[0])
        break
      default:
        if (
          values.length === 1 &&
          (values[0] === 'true' || values[0] === 'false')
        ) {
          const booleanFilter: AttributeWithBooleanValueFilter = {
            type: 'boolean',
            key: filterName,
            value: values[0] === 'true',
          }
          return productSearchQuery.attributes?.push(booleanFilter)
        } else {
          const attributeFilter: AttributeWithValuesFilter = {
            type: 'attributes',
            key: filterName,
            values: values as any, // String values for Odoscope
          }

          return productSearchQuery.attributes?.push(attributeFilter)
        }
    }
  })

  return productSearchQuery
}

export default (_query?: LocationQuery) => {
  const route = useRoute()

  const query = computed(() => _query || route.query)

  const appliedFilter = computed<ProductSearchQuery>(() => {
    return parseOdoscopeSearchAppliedFilters(query.value)
  })

  const appliedFiltersCount = computed<number>(() => {
    let count = 0

    // Add all of the different attribute/boolean filters to the count
    count += appliedFilter.value.attributes?.length ?? 0

    // Count the minPrice/maxPrice filters as one filter
    if (
      appliedFilter.value.minPrice !== undefined ||
      appliedFilter.value.maxPrice !== undefined
    ) {
      count += 1
    }

    return count
  })

  const appliedAttributeValues = computed(() => {
    const values: Record<string, Array<string>> = {}

    appliedFilter.value?.attributes?.forEach((attribute) => {
      if (attribute.type !== 'attributes' || !attribute.key) {
        return
      }

      values[attribute.key] = attribute.values as string[]
    })
    return values
  })

  const appliedBooleanValues = computed(() => {
    const values: Record<string, boolean> = {}

    appliedFilter.value?.attributes?.forEach((attribute) => {
      if (attribute.type === 'boolean') {
        values[attribute.key] = attribute.value
      }
    })
    return values
  })

  const areFiltersApplied = computed<boolean>(() => {
    return appliedFiltersCount.value > 0
  })

  return {
    appliedFilter,
    appliedFiltersCount,
    appliedAttributeValues,
    appliedBooleanValues,
    areFiltersApplied,
  }
}
